import React, { useState } from 'react'
import { Box, IconButton, Tab, Tabs, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { DashboardPage } from '../../../types/gotypes'

interface BuilderTabsProps {
  pages: DashboardPage[]
  activePageIndex: number
  onSelect: (index: number) => void
  onAdd: () => void
  onRename: (index: number, name: string) => void
  onDelete: (index: number) => void
}

/**
 * The layout builder's page/tab bar. Add and inline-rename are required by
 * the issue's acceptance criteria; reordering tabs is not (only widget
 * reorder is), so this stays deliberately simple — no drag here.
 */
const BuilderTabs: React.FC<BuilderTabsProps> = ({ pages, activePageIndex, onSelect, onAdd, onRename, onDelete }) => {
  const [renamingIndex, setRenamingIndex] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const startRename = (index: number) => {
    setRenamingIndex(index)
    setRenameValue(pages[index].name)
  }

  const commitRename = () => {
    if (renamingIndex !== null && renameValue.trim()) {
      onRename(renamingIndex, renameValue.trim())
    }
    setRenamingIndex(null)
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', px: 1 }}>
      <Tabs value={activePageIndex} onChange={(_e, v) => onSelect(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40 }}>
        {pages.map((page, i) => (
          <Tab
            key={page.id}
            value={i}
            sx={{ minHeight: 40, py: 0.5, textTransform: 'none' }}
            onDoubleClick={() => startRename(i)}
            label={
              renamingIndex === i ? (
                <TextField
                  autoFocus
                  size="small"
                  variant="standard"
                  value={renameValue}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename()
                    if (e.key === 'Escape') setRenamingIndex(null)
                  }}
                  sx={{ width: 100 }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {page.name}
                  {pages.length > 1 && (
                    <Box
                      component="span"
                      role="button"
                      aria-label={`Delete ${page.name}`}
                      onClick={(e) => { e.stopPropagation(); onDelete(i) }}
                      sx={{ display: 'inline-flex', ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
              )
            }
          />
        ))}
      </Tabs>
      <Tooltip title="Add tab">
        <IconButton size="small" aria-label="Add tab" onClick={onAdd} sx={{ ml: 1 }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default BuilderTabs
